import i18n from "i18next";
import axios from "axios";

import { create, type UseBoundStore, type StoreApi } from "zustand";
import { verifyToken, getAchievements, getYaml, getModuleProgresses, getConfigJson, getLTIQuizzesList, getStemboxBranches } from "@/api";
import { v4 as uuid } from "uuid";
import { parse } from 'yaml'
import {
  getSubCookieDomain,
  getCookie,
  setCookie,
  SessionError,
  AchievementError,
  isPublicProject,
  postMessageToRef,
  BlocklyHighlightError,
  createEncryptor,
  getGodotBaseUrl,
  isElectronShell
} from "@/utils";

import {
  type IJsonModel,
  Model,
  Actions,
  DockLocation,
} from "flexlayout-react";

import { type ConfigJson, type Yaml } from "@/types";

import * as Sentry from "@sentry/react";
import localYaml from "@/local.yaml?raw"

import achievementMappingJson from "@/utils/achievementsMapping.json"
import qaMappingJson from "@/utils/qaMapping.json"

interface User {
  id: number;
  full_name: string;
  username: string;
  email: string;
  gsm: string | null;
  default_group: number;
  avatar: string;
  first_name: string;
  last_name: string;
  primary_account: number;
  secondary_account: number;
  credit: {
    balance: number;
    detail: null;
  };
  open_editor_limit: null | number;
  cms_info: {
    is_student: boolean;
    is_teacher: boolean;
    is_demo_user: boolean;
    is_branch_manager: boolean;
    is_organization_manager: boolean;
  };
  is_superuser: boolean;
}

interface SessionUser extends User {
  userFrom: string;
  tenantType: "corporate" | "individual";
  roles: string[],
}

interface RidersUser extends User {
  is_product_walkthrough_shown: string,
  license_types: string[],
  roles: string[],
  platform_language: {
    language: string,
    detail: string
  }
}

interface Project {
  stembox_branch: string,
  project_id: string,
  project_type: "playable" | "design" | "review" | "module",
  backwardCompatibility?: boolean,
  editorLanguage?: "en" | "tr"
}

interface Metadata {
  storage: "git" | "s3" | "none";
  session_id: string;
  git_address: string;
  git_branch_name: string;
  active_sessions: string[];
  is_preview: boolean;
  isProjectOwner: boolean;
  cms: null | {
    course_id: number;
    course_slug: string;
    lecture_name_tr?: string;
    lecture_name_en?: string;
    cms_info: {
      branch_id: number;
      branch_slug: string;
      classroom_id: number;
      classroom_slug: string;
      organization_id: number;
      organization_slug: string;
    };
  };
};

interface Tokens {
  express_api_token?: string
}

interface URLParams {
  previousPath?: string;
  classroomSlug?: string;
  projectSlug?: string;
}

interface Achievement {
  id: number | string;
  slug: string | null;
  name: string;
  description: string;
  is_achieved: boolean;
};

type Block = {
  type: string;
  id?: string;
  index?: number;
  name?: string;
  config?: any;
};

type WorkspaceLayout = {
  simulationWorkspace: {
    blocks: Block[];
    width?: number;
    height?: number;
  };
  mainWorkspace: {
    blocks: Block[];
    width?: number;
    height?: number;
  };
  secondaryWorkspace: {
    blocks: Block[];
    width?: number;
    height?: number;
  };

  [workspaceId: string]: {
    blocks: Block[];
    weight?: number;
    width?: number;
    height?: number;
    dockLocation?: "left" | "right" | "top" | "bottom" | "center";
  };
};

type Tabs = {
  type: string;
  id: string;
}[];

interface Layout {
  id: string | undefined;
  model: Model;
  modelJson: IJsonModel;
  tabs: Tabs;
  previousLayout: string | null;

}

type CircleFlowSubcircleType = "Stage" | "Relic";

export interface CircleFlowSubcircle {
  index: number;
  type: CircleFlowSubcircleType;
  module: string;
  stage: number;
  title: string;
  icon: string;
  editor: boolean;
  independent: boolean;
  isolated: boolean;
  locked: boolean;
  completed: boolean;
  selected: boolean;
  visited: boolean;
  loading: boolean;
  checkpoint_id: string | number | null;
}

export interface CircleFlowGroup {
  name: string | null;
  group_index: number;
  active: boolean;
  completed: boolean;
  count_in_group: number;
  count_completed: number;
  progress_percent: number;
  title?: string; // not yet sent by Godot; optional until the data contract adds it
  subcircles: CircleFlowSubcircle[];
}

interface BlocklyVariables {
  [key: string]: { [key: string]: any }
}

/**
 * Where the editor is running.
 *
 * `electron` is the desktop shell (see desktop/README.md). It is a platform of
 * its own rather than a flavour of `riders` because the shell has no browser
 * chrome of its own and draws a different editor -- but it still authenticates
 * exactly like `riders` does, which is what RIDERS_AUTH_PLATFORMS below is for.
 */
export type Platform = "riders" | "mebkit" | "lti" | "electron";

/**
 * Platforms whose sessions are proved by a riders.ai login cookie, and which
 * therefore run initRidersUser() and authorize().
 *
 * The desktop shell belongs here: it is handed a real `sessionid` for
 * `.riders.ai` through the handoff exchange before the page is ever loaded,
 * precisely so that authorize() can still check the cookie's owner against the
 * session behind the token. Dropping it out of these checks would turn a
 * copied token into a working one on the desktop -- see the "Authentication"
 * section of desktop/README.md.
 */
const RIDERS_AUTH_PLATFORMS: Platform[] = ["riders", "electron"];

export const usesRidersAuth = (platform?: Platform): boolean =>
  !!platform && RIDERS_AUTH_PLATFORMS.includes(platform);

interface EditorState {
  init: (token: string) => Promise<void>,
  initialized: boolean,

  environment?: "local" | "staging" | "production",
  initEnvironment: () => void,

  platform?: Platform,
  initPlatform: () => void,

  theme: "light" | "dark" | "maarif";
  setTheme: (theme: "light" | "dark" | "maarif") => void;
  initTheme: () => void;

  mode: "editor" | "preview";
  initMode: () => void;
  setMode: (mode: "editor" | "preview") => void;

  language: "en" | "tr";
  setLanguage: (lang: "en" | "tr" | "en-US") => void;
  initLanguage: () => void;

  saveStatus: "SAVING" | "SAVED" | "NEEDSAVE";
  setSaveStatus: (status: "SAVING" | "SAVED" | "NEEDSAVE") => void;

  blocklyCodePath: string;
  setBlocklyCodePath: (path: string) => void;

  pythonCodePath: string;
  setPythonCodePath: (path: string) => void;

  shutDown: boolean;
  setShutDown: (shutDown: boolean) => void;

  godotContainerRef: React.RefObject<any> | null;
  setGodotContainerRef: (ref: React.RefObject<any>) => void;

  documentationRefs: Map<string, HTMLIFrameElement>;
  registerDocumentationRef: (id: string, el: HTMLIFrameElement) => void;
  unregisterDocumentationRef: (id: string) => void;

  isBackButtonPressed: boolean;
  setIsBackButtonPressed: (isBackButtonPressed: boolean) => void;

  blocklyRefreshKey: string;
  isBlocklyRefreshValid: boolean;
  setBlocklyRefreshKey: () => void;

  verifyResponse?: any
  sendVerifyRequest: () => Promise<void>,

  sessionUser?: SessionUser,
  initSessionUser: () => Promise<void>,
  setSessionUser: (user: SessionUser) => void,

  ridersUser?: RidersUser,
  initRidersUser: () => Promise<void>,
  setRidersUser: (user: RidersUser) => void,

  project?: Project,
  initProject: () => Promise<void>,
  setProject: (project: Project) => void,

  metadata?: Metadata,
  initMetadata: () => Promise<void>,
  setMetadata: (metadata: Metadata) => void,

  tokens?: Tokens,
  setTokens: (tokens: Tokens) => void,

  urlParams?: URLParams,
  initUrlParams: () => Promise<void>,
  setUrlParams: (params: URLParams) => void,

  achievements?: Achievement[],
  initAchievements: () => Promise<void>,
  setAchievements: (achievements: Achievement[]) => void,
  achieve: (id: number | string) => void,

  sessionError?: string;
  setSessionError: (error: string) => void;

  layout: Layout;
  initLayout: () => void;
  pushTab: (tab: { type: string; id: string }) => void;
  resetLayout: () => void;
  setLayout: (layout: WorkspaceLayout) => void;
  codeTabWidth: number;
  setCodeTabWidth: (width: number) => void;
  codeTabHeight: number;
  setCodeTabHeight: (height: number) => void;

  blocklyVariables: BlocklyVariables;
  setBlocklyVariables: (params: { scope: string; state: string; newData: any }) => void;
  getBlocklyVariables: (scope: string, state: string) => any;
  initBlocklyVariables: (d?: any) => void;
  subscribeBlocklyVariables: (callback: (state: BlocklyVariables) => void) => () => void;
  initBlocklyFiles: () => Promise<void>;
  generateBlocksFile: string
  initBlocksFile: string
  toolboxFile: string

  toJson: () => string;

  authorize: () => Promise<void>;

  backwardCompatibility: boolean;
  yamlData: Yaml | undefined;
  initYamlData: () => Promise<void>;
  setYamlData: (yamlData: string) => void;

  configJson: ConfigJson | undefined;
  initConfigJson: () => Promise<void>;

  progressedChapters: number[];
  initProgressedChapters: () => Promise<void>;

  collapsedSidebar: boolean;
  setCollapsedSidebar: (collapsedSidebar: boolean) => void;

  blocklyState: "NOTINITED" | "REFRESHING" | "INITED";
  setBlocklyState: (blocklyState: "NOTINITED" | "REFRESHING" | "INITED") => void;
  highLightQueue: any[];
  highlightBlock: (message: any) => Promise<void>;
  highlightToolboxBlock: (message: any) => Promise<void>;

  ltiAchievementRestore: () => void;
  prevEditorState: any;
  setPrevEditorState: (prevEditorState: any) => void;

  circleFlow: CircleFlowGroup[] | null;
  setCircleFlow: (circleFlow: CircleFlowGroup[] | null) => void;

  // Optimistic "a subcircle navigation was just triggered" state, shared
  // between CircleFlowBar (per-node loading style) and LayoutLoadingOverlay
  // (full Layout cover), since Godot's `loading` echo in setCircleFlow is
  // not reliable enough to be the sole source of truth.
  circleFlowPendingIndex: number | null;
  setCircleFlowPendingIndex: (index: number | null) => void;
  circleFlowGroupNavigatePending: boolean;
  setCircleFlowGroupNavigatePending: (pending: boolean) => void;

  // Whether the current Godot build serves a version.json at its base URL —
  // used as a signal that it supports the godotLoadingProgress/
  // godotLoadingComplete message protocol. backwardCompatibility (v3) builds
  // predate that protocol and never emit these events, so they're excluded.
  godotVersionedBuild: boolean;
  // Set once the Godot iframe reports godotLoadingComplete.
  godotLoadingComplete: boolean;

  encryptMessage: (message: any) => string;
  decryptMessage: (message: any) => string;

  achievementMapping: { [key: string]: string };
  initAchievementMapping: () => void;
  parseAchievementId: (messageData: any) => string | number

  qaMapping: { [key: string]: string };
  initQAMapping: () => void;
  parseQAId: (messageData: any) => string | number

  ltiQuizResults: { [quizId: string]: { question: number, answer: number, correct_answer: number }[] };
  initLtiQuizResults: () => void;
  setLtiQuizResults: (quizId: string, results: { question: number, answer: number, correct_answer: number }[]) => void;

  ltiQuizzesList: string[];
  initLtiQuizzesList: () => void;

  calculateLtiScore: () => number;

  initGodotVersionCheck: () => Promise<void>;
}

export const useEditorState: UseBoundStore<StoreApi<EditorState>> = create<EditorState>()((set, get) => ({

  initialized: false,
  init: async (token: string) => {
    const {
      initSessionUser,
      initRidersUser,
      initProject,
      initMetadata,
      initUrlParams,
      initAchievements,
      setTokens,
      sendVerifyRequest,
      initEnvironment,
      initPlatform,
      initTheme,
      initLanguage,
      initBlocklyVariables,
      initLayout,
      authorize,
      initYamlData,
      initProgressedChapters,
      initConfigJson,
      initBlocklyFiles,
      initAchievementMapping,
      initQAMapping,
      initLtiQuizzesList,
      initMode,
      initGodotVersionCheck,
    } = get();

    // Function calling order matters!
    setTokens({ express_api_token: token });
    await sendVerifyRequest();
    initSessionUser();
    initProject();
    initMetadata();
    initMode();
    initUrlParams();
    initPlatform();
    initLanguage();
    initEnvironment();
    initTheme();
    initBlocklyVariables();
    initAchievementMapping();
    initQAMapping();

    await Promise.all([
      initRidersUser(),
      initAchievements(),
      initLtiQuizzesList(),       // LTI projects only
      initGodotVersionCheck(),
    ]);

    // For v3 projects (backward compatibility)
    if (get().project?.backwardCompatibility) {
      set({ backwardCompatibility: true });
      await Promise.all([
        initYamlData(),
        initProgressedChapters(),
        initConfigJson(),
      ])
      await initBlocklyFiles();
    }

    if (!get().ridersUser?.is_superuser) {
      await authorize();
    }

    initLayout();

  },

  sessionUser: undefined,
  initSessionUser: async () => {
    const { verifyResponse } = get();
    let tenantType: "corporate" | "individual" = "corporate";
    if (
      verifyResponse?.cms?.[0]?.cms_info?.classroom_type &&
      verifyResponse?.cms?.[0]?.cms_info?.classroom_type.length > 0
    ) {
      if (
        verifyResponse?.cms?.[0]?.cms_info?.classroom_type.includes("individual")
      ) {
        tenantType = "individual";
      } else {
        tenantType = "corporate";
      }
    } else if (verifyResponse?.user?.license_types?.includes("individual")) {
      tenantType = "individual";
    }
    if (verifyResponse?.user) {
      set({ sessionUser: { ...verifyResponse.user, tenantType, roles: verifyResponse.user.roles } });
    }

  },
  setSessionUser: (user: SessionUser) => {
    set({ sessionUser: user });
  },

  ridersUser: undefined,
  initRidersUser: async () => {
    const { verifyResponse, platform, environment } = get();

    if (environment === "local" || !usesRidersAuth(platform)) {
      return;
    }

    // TODO: get riders user from api
    // Second check if user is superuser. If yes, resolve because superuser can access any session.
    try {
      const { data: riders_user_data } = await axios.get(
        `https://${import.meta.env.VITE_COLABSERVER_URL}/api/v1/user`,
        {
          withCredentials: true,
        }
      );
      if (Array.isArray(riders_user_data) && riders_user_data.length > 0) {
        set({ ridersUser: riders_user_data[0] });
      }
    } catch (e) {
      throw new SessionError("invalidToken.loginRequired", e as Error);
    }


  },
  setRidersUser: (user: RidersUser) => {
    set({ ridersUser: user });
  },

  project: undefined,
  initProject: async () => {
    const { verifyResponse } = get();

    const project: Project = {
      project_id: verifyResponse?.project_id,
      stembox_branch: verifyResponse?.project?.stemboxBranch,
      project_type: verifyResponse?.cms?.[0]?.lecture_type || "module",
      backwardCompatibility: verifyResponse?.project?.backwardCompatibility,
      editorLanguage: verifyResponse?.project?.editorLanguage?.toLowerCase(),
    }

    set({ project });
  },
  setProject: (project: Project) => {
    set({ project });
  },

  tokens: undefined,
  setTokens: (tokens: Tokens) => {
    set({ tokens });
  },

  urlParams: undefined,
  initUrlParams: async () => {
    const { urlParams } = get();
    if (urlParams) return;

    const path = window.location.href;

    const url = new URL(path);
    const previousPath = url.searchParams.get("previous") || undefined;
    const classroomSlug = url.searchParams.get("classroom") || undefined;
    const projectSlug = url.searchParams.get("project") || undefined;

    set({ urlParams: { previousPath, classroomSlug, projectSlug } });
  },
  setUrlParams: (params: URLParams) => {
    set({ urlParams: params });
  },

  achievements: [],
  initAchievements: async () => {
    try {
      const { language, ltiAchievementRestore, platform, setAchievements } = get();

      const { data: achievements } = await getAchievements(
        get().tokens?.express_api_token || "",
        language,
      );
      setAchievements(achievements);

      if (platform === "lti") {
        ltiAchievementRestore();
      }

    } catch (error) {
      set({ achievements: [] });
      Sentry.captureException(new AchievementError("Failed to fetch achievements", error as Error));
    }
  },
  setAchievements: (achievements: Achievement[]) => {
    const modifiedAchievements = achievements.map((achievement) => {
      //Merge achievement.slug into achievement.id if slug exists
      if (achievement.slug) {
        achievement.id = achievement.slug;
      }
      return achievement;
    });
    set({ achievements: modifiedAchievements });
  },
  achieve: (id: number | string) => {
    const currentAchievements = get().achievements || [];
    const updatedAchievements = currentAchievements.map((achievement) =>
      achievement.id === id
        ? { ...achievement, is_achieved: true }
        : achievement,
    );
    set({ achievements: updatedAchievements });

    //Send achieve message to all documentation iframs
    if (get().backwardCompatibility) {
      const refs = get().documentationRefs;
      const pseudoRefs = Array.from(refs.values()).map(el => ({ current: { contentWindow: el.contentWindow } }));
      postMessageToRef(pseudoRefs, {
        action: "achieve",
        data: id,
      });
    }
  },

  sessionError: undefined,
  setSessionError: (error: string) => {
    set({ sessionError: error });
  },

  sendVerifyRequest: async () => {
    const { tokens } = get();

    try {
      const authData = await verifyToken(tokens?.express_api_token || "");
      set({ verifyResponse: authData });
      set({ tokens: { ...tokens, express_api_token: authData.accessToken } });
    } catch (error) {
      throw new SessionError("invalidToken.sessionExpired", error as Error);
    }
  },

  initEnvironment: () => {
    const host = window.location.hostname;
    if (host.includes("staging")) {
      set({ environment: "staging" });
    } else if (host.includes("localhost")) {
      set({ environment: "local" });
    } else {
      set({ environment: "production" });
    }
  },

  initPlatform: () => {
    const { verifyResponse } = get();
    const host = window.location.hostname;

    if (verifyResponse?.lti_launch_id) {
      set({ platform: "lti" });
    } else if (isElectronShell()) {
      // Checked before the hostname, because the desktop shell serves this
      // very page from editor.riders.ai -- host alone would call it "riders"
      // and the editor would render its browser chrome inside an app window
      // that has no use for it.
      //
      // LTI still wins over it: an LTI launch is about who the session belongs
      // to, which does not stop being true because it happens in the shell.
      set({ platform: "electron" });
    } else if (host.includes("riders")) {
      set({ platform: "riders" });
    } else if (host.includes("mebkit")) {
      set({ platform: "mebkit" });
    }

  },

  metadata: undefined,
  initMetadata: async () => {
    const { verifyResponse } = get();

    const metadata: Metadata = {
      storage: verifyResponse?.git_address?.startsWith("s3://")
        ? "s3"
        : verifyResponse?.storage,
      session_id: verifyResponse?.session_id,
      git_address: verifyResponse?.git_address,
      git_branch_name: verifyResponse?.git_branch_name,
      active_sessions: verifyResponse?.active_sessions || [],
      is_preview: verifyResponse?.is_preview,
      isProjectOwner: verifyResponse?.session_user_name === verifyResponse?.user?.username,
      cms: verifyResponse?.cms?.[0] || null,
    }

    set({ metadata });
  },
  setMetadata: (metadata: Metadata) => {
    set({ metadata });
  },

  theme: "light",
  setTheme: (theme: "light" | "dark" | "maarif") => {
    document.documentElement.classList.remove("dark", "light");
    if (theme === "maarif") {
      document.documentElement.setAttribute("data-theme", "maarif");
    } else {
      document.documentElement.removeAttribute("data-theme");
      document.documentElement.classList.add(theme);
      setCookie("theme", theme, { domain: getSubCookieDomain() });
    }

    set(() => ({
      theme,
      blocklyRefreshKey: uuid(),
    }));
  },
  initTheme: () => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlTheme = urlParams.get("theme");

    if (urlTheme === "maarif") {
      document.documentElement.setAttribute("data-theme", "maarif");
      set({ theme: "maarif" });
      return;
    }

    const theme = getCookie("theme") || "light";
    document.documentElement.classList.add(theme);
    set({ theme: theme as "light" | "dark" });
  },

  mode: "editor",
  initMode: () => {
    const { metadata } = get();
    if (!metadata) return;

    let mode: "editor" | "preview" = "editor";
    if (metadata?.is_preview) {
      mode = "preview";
    }

    set({ mode });
  },
  setMode: (mode: "editor" | "preview") => {
    set({ mode });
  },

  language: "tr",
  setLanguage: (lang: "en" | "tr" | "en-US") => {
    if (lang === "en-US" || lang.startsWith("en-")) lang = "en";
    if (lang === "tr-TR" || lang.startsWith("tr-")) lang = "tr";
    setCookie("accept-language", lang, { domain: getSubCookieDomain() });
    set(() => ({ language: lang }));
    i18n.changeLanguage(lang);
  },

  initLanguage: () => {
    const { platform } = get();
    let language = getCookie("accept-language") || navigator.language || "en";

    if (platform === "lti") {
      language = get().project?.editorLanguage || navigator.language || "en";
    }

    get().setLanguage(language as "en" | "tr" | "en-US");
  },

  shutDown: false,
  setShutDown: (shutDown: boolean) => {
    set({ shutDown });
  },

  godotContainerRef: null,
  setGodotContainerRef: (ref: React.RefObject<any>) => {
    set({ godotContainerRef: ref });
  },

  documentationRefs: new Map<string, HTMLIFrameElement>(),
  registerDocumentationRef: (id: string, el: HTMLIFrameElement) => {
    get().documentationRefs.set(id, el);
  },
  unregisterDocumentationRef: (id: string) => {
    get().documentationRefs.delete(id);
  },

  isBackButtonPressed: false,
  setIsBackButtonPressed: (isBackButtonPressed: boolean) => {
    set({ isBackButtonPressed });
  },

  blocklyRefreshKey: uuid(),
  isBlocklyRefreshValid: true,
  setBlocklyRefreshKey: () => {
    if (!get().isBlocklyRefreshValid) return
    set({ blocklyRefreshKey: uuid(), isBlocklyRefreshValid: false, blocklyState: "REFRESHING" });

  },

  layout: {
    id: uuid(),
    modelJson: {} as IJsonModel,
    model: {} as Model,
    tabs: [],
    previousLayout: null,
  },
  initLayout: () => {
    const isSuperUser =
      get().sessionUser?.is_superuser &&
      window?.location?.search?.includes("staff");

    const initialLayout: IJsonModel = {
      global: {
        tabEnablePopout: false,
        splitterEnableHandle: true,
        tabSetMinWidth: 130,
        tabSetMinHeight: 100,
        borderMinSize: 100,
        tabSetEnableTabScrollbar: true,
        borderEnableTabScrollbar: true,
        tabSetEnableSingleTabStretch: true,
      },
      borders: isSuperUser
        ? [
          {
            type: "border",
            location: "bottom",
            children: [
              {
                type: "tab",
                name: "States",
                component: "json",
                enableClose: false,
              },
              {
                type: "tab",
                name: "FS Viewer",
                component: "fsViewer",
                enableClose: false,
              },
              {
                type: "tab",
                name: "Log Store",
                component: "logStore",
                enableClose: false,
              },
            ],
          },
        ]
        : [],
      layout: {
        id: "main-row",
        type: "row",
        weight: 100,
        children: [
          {
            id: "simulation-workspace",
            type: "tabset",
            children: [
              {
                id: "main-godot-tab",
                type: "tab",
                name: "",
                component: !get().backwardCompatibility ? "Simulation" : "unknown",
                enableClose: false,
              },
            ],
          },
        ],
      },
    };
    set({
      layout: {
        ...get().layout,
        modelJson: initialLayout,
        model: Model.fromJson(initialLayout),
        tabs: [
          {
            type: "godot",
            id: "main-godot-tab",
          },
        ],
      },
    });
  },
  pushTab: (tab: { type: string; id: string }) => {
    const currentTabs = get().layout.tabs;
    if (!currentTabs.some((t) => t.id === tab.id)) {
      set((state) => ({
        layout: {
          ...state.layout,
          tabs: [...state.layout.tabs, tab],
        },
      }));
    }
  },
  resetLayout: () => {
    const model = get().layout.model;
    const tabs = get().layout.tabs;

    get().setBlocklyCodePath("");
    get().setPythonCodePath("");

    if (!get().backwardCompatibility) {
      tabs
        .filter((tab) => tab.id !== "main-godot-tab")
        .forEach((tab) => {
          model.doAction(Actions.deleteTab(tab.id));
        });
    } else {
      tabs
        .forEach((tab) => {
          model.doAction(Actions.deleteTab(tab.id));
        });
    }

    set({
      layout: {
        ...get().layout,
        tabs: !get().backwardCompatibility ? [
          {
            type: "Simulation",
            id: "main-godot-tab",
          },
        ] : [],
      },
    });

  },
  setLayout: (layout: WorkspaceLayout) => {
    const layoutString = JSON.stringify(layout);
    if (layoutString === get().layout.previousLayout) {
      return;
    }
    set({ layout: { ...get().layout, previousLayout: layoutString, id: uuid() } });

    get().resetLayout();
    const model = get().layout.model;
    const pushTab = get().pushTab;

    let simulationWorkspace = "simulation-workspace";
    let mainWorkspaceId = "main-workspace";
    let secondaryWorkspaceId = "secondary-workspace";


    if (layout.simulationWorkspace.blocks.length > 0) {
      const block = layout.simulationWorkspace.blocks[0];
      if (!get().backwardCompatibility) {
        model.doAction(
          Actions.updateNodeAttributes("main-godot-tab", {
            name: block.name || block.type,
          })
        );
        layout.simulationWorkspace.blocks
          .filter((b) => b.id !== "main-godot-tab")
          .sort((a, b) => {
            return (a.index ?? 0) - (b.index ?? 0);
          })
          .reverse()
          .forEach((block) => {
            const id = block.id || uuid();
            model.doAction(
              Actions.addNode(
                {
                  id: id,
                  type: "tab",
                  component: block.type,
                  name: block.name || block.type,
                  config: block.config,
                  enableClose: false,
                },
                simulationWorkspace,
                DockLocation.CENTER,
                0
              )
            );
            pushTab({
              type: block.type,
              id: id,
            });
          });
      } else {
        model.doAction(
          Actions.addNode(
            {
              id: "main-godot-tab",
              type: "tab",
              component: block.type,
              name: block.name || block.type,
              config: block.config,
              enableClose: false,
            },
            "main-row",
            DockLocation.CENTER,
            0
          )
        );
        pushTab({
          type: block.type,
          id: "main-godot-tab",
        });
        simulationWorkspace = model.getFirstTabSet()?.getId() || simulationWorkspace;
      }
    }

    if (layout.mainWorkspace.blocks.length > 0) {
      layout.mainWorkspace.blocks
        .sort((a, b) => {
          return (a.index ?? 0) - (b.index ?? 0);
        })
        .reverse()
        .forEach((block, index) => {
          const id = block.id || uuid();
          if (block.type === "Blockly") {
            get().setBlocklyCodePath(block.config?.xml_file_path);
          }
          if (block.type === "Editor") {
            get().setPythonCodePath(block.config?.file_path);
          }

          if (index === 0) {
            model.doAction(
              Actions.addNode(
                {
                  id: id,
                  type: "tab",
                  component: block.type,
                  name: block.name || block.type,
                  config: block.config,
                  enableClose: false,
                },
                "main-row",
                DockLocation.LEFT,
                0
              )
            );
            mainWorkspaceId =
              model.getActiveTabset()?.getId() || "main-workspace";
          } else {
            model.doAction(
              Actions.addNode(
                {
                  id: id,
                  type: "tab",
                  component: block.type,
                  name: block.name || block.type,
                  config: block.config,
                  enableClose: false,
                },
                mainWorkspaceId,
                DockLocation.CENTER,
                0
              )
            );
          }
          pushTab({
            type: block.type,
            id: id,
          });
        });
    }

    const hasMain = layout.mainWorkspace.blocks.length > 0;

    if (layout.secondaryWorkspace.blocks.length > 0) {
      layout.secondaryWorkspace.blocks
        .sort((a, b) => {
          return (a.index ?? 0) - (b.index ?? 0);
        })
        .reverse()
        .forEach((block, index) => {
          const id = block.id || uuid();
          if (index === 0) {
            model.doAction(
              Actions.addNode(
                {
                  id: id,
                  type: "tab",
                  component: block.type,
                  name: block.name || block.type,
                  config: block.config,
                  enableClose: false,
                  weight: 30,
                },
                hasMain ? mainWorkspaceId : "main-row",
                hasMain ? DockLocation.BOTTOM : DockLocation.LEFT,
                0
              )
            );
            secondaryWorkspaceId =
              model.getActiveTabset()?.getId() || "secondary-workspace";
          } else {
            model.doAction(
              Actions.addNode(
                {
                  id: id,
                  type: "tab",
                  component: block.type,
                  name: block.name || block.type,
                  config: block.config,
                },
                secondaryWorkspaceId,
                DockLocation.CENTER,
                0
              )
            );
          }
          pushTab({
            type: block.type,
            id: id,
          });
        });
    }

    if (hasMain || layout.secondaryWorkspace.blocks.length > 0) {
      model.doAction(
        Actions.adjustWeights("main-row", [
          get().codeTabWidth,
          100 - get().codeTabWidth,
        ])
      );

      if (hasMain && layout.secondaryWorkspace.blocks.length > 0) {
        const leftWorkspaceId = model
          .getNodeById(mainWorkspaceId)
          ?.getParent()
          ?.getId();

        model.doAction(
          Actions.adjustWeights(leftWorkspaceId!, [
            get().codeTabHeight,
            100 - get().codeTabHeight,
          ])
        );
      }
    }

    if (get().backwardCompatibility) {
      if (
        layout.simulationWorkspace.blocks.length === 0
        && layout.mainWorkspace.blocks.length === 0
        && layout.secondaryWorkspace.blocks.length === 0
      ) {
        const modelJson = model.toJson();
        modelJson.layout.children = [];

        const topRow: any[] = []
        const leftRow: any[] = []
        const centerRow: any[] = []
        const rightRow: any[] = []
        const bottomRow: any[] = []

        const topRowTopBlock: any[] = []
        const topRowLeftBlock: any[] = []
        const topRowCenterBlock: any[] = []
        const topRowRightBlock: any[] = []
        const topRowBottomBlock: any[] = []

        const leftRowTopBlock: any[] = []
        const leftRowLeftBlock: any[] = []
        const leftRowCenterBlock: any[] = []
        const leftRowRightBlock: any[] = []
        const leftRowBottomBlock: any[] = []

        const centerRowTopBlock: any[] = []
        const centerRowLeftBlock: any[] = []
        const centerRowCenterBlock: any[] = []
        const centerRowRightBlock: any[] = []
        const centerRowBottomBlock: any[] = []

        const rightRowTopBlock: any[] = []
        const rightRowLeftBlock: any[] = []
        const rightRowCenterBlock: any[] = []
        const rightRowRightBlock: any[] = []
        const rightRowBottomBlock: any[] = []

        const bottomRowTopBlock: any[] = []
        const bottomRowLeftBlock: any[] = []
        const bottomRowCenterBlock: any[] = []
        const bottomRowRightBlock: any[] = []
        const bottomRowBottomBlock: any[] = []



        Object.keys(layout).filter((key) => {
          return key !== "simulationWorkspace" && key !== "mainWorkspace" && key !== "secondaryWorkspace";
        }).forEach((key) => {

          if (layout[key].dockLocation === "top") {
            topRow.push(layout[key]);

            layout[key].blocks.forEach((block, index) => {
              if (block.config?.dockLocation === "top") {
                topRowTopBlock.push(block);
              } else if (block.config?.dockLocation === "left") {
                topRowLeftBlock.push(block);
              } else if (block.config?.dockLocation === "right") {
                topRowRightBlock.push(block);
              } else if (block.config?.dockLocation === "bottom") {
                topRowBottomBlock.push(block);
              } else if (block.config?.dockLocation === "center") {
                topRowCenterBlock.push(block);
              }
            })
          } else if (layout[key].dockLocation === "left") {
            leftRow.push(layout[key]);

            layout[key].blocks.forEach((block, index) => {
              if (block.config?.dockLocation === "top") {
                leftRowTopBlock.push(block);
              } else if (block.config?.dockLocation === "left") {
                leftRowLeftBlock.push(block);
              } else if (block.config?.dockLocation === "right") {
                leftRowRightBlock.push(block);
              } else if (block.config?.dockLocation === "bottom") {
                leftRowBottomBlock.push(block);
              } else if (block.config?.dockLocation === "center") {
                leftRowCenterBlock.push(block);
              }
            })

          } else if (layout[key].dockLocation === "center") {
            centerRow.push(layout[key]);

            layout[key].blocks.forEach((block, index) => {
              if (block.config?.dockLocation === "top") {
                centerRowTopBlock.push(block);
              } else if (block.config?.dockLocation === "left") {
                centerRowLeftBlock.push(block);
              } else if (block.config?.dockLocation === "right") {
                centerRowRightBlock.push(block);
              } else if (block.config?.dockLocation === "bottom") {
                centerRowBottomBlock.push(block);
              } else if (block.config?.dockLocation === "center") {
                centerRowCenterBlock.push(block);
              }
            })

          } else if (layout[key].dockLocation === "right") {
            rightRow.push(layout[key]);

            layout[key].blocks.forEach((block, index) => {
              if (block.config?.dockLocation === "top") {
                rightRowTopBlock.push(block);
              } else if (block.config?.dockLocation === "left") {
                rightRowLeftBlock.push(block);
              } else if (block.config?.dockLocation === "right") {
                rightRowRightBlock.push(block);
              } else if (block.config?.dockLocation === "bottom") {
                rightRowBottomBlock.push(block);
              } else if (block.config?.dockLocation === "center") {
                rightRowCenterBlock.push(block);
              }
            })

          } else if (layout[key].dockLocation === "bottom") {
            bottomRow.push(layout[key]);

            layout[key].blocks.forEach((block, index) => {
              if (block.config?.dockLocation === "top") {
                bottomRowTopBlock.push(block);
              } else if (block.config?.dockLocation === "left") {
                bottomRowLeftBlock.push(block);
              } else if (block.config?.dockLocation === "right") {
                bottomRowRightBlock.push(block);
              } else if (block.config?.dockLocation === "bottom") {
                bottomRowBottomBlock.push(block);
              } else if (block.config?.dockLocation === "center") {
                bottomRowCenterBlock.push(block);
              }
            })
          }
        });

        topRow.forEach((tabset, index) => {
          modelJson.layout.children.push(
            {
              type: "row",
              weight: tabset.weight || 100,
              children: [
                {
                  type: "row",
                  weight: 100,
                  children: [
                    {
                      type: "tabset",
                      id: tabset.id,
                      weight: topRowTopBlock.length > 0 ? (topRowTopBlock[0].config?.weight || 100) : 100,
                      children: topRowTopBlock.map((block, blockIndex) => {
                        const blockId = block.id || uuid();
                        pushTab({
                          type: block.type,
                          id: blockId,
                        });
                        return {
                          type: "tab",
                          id: blockId,
                          component: block.type,
                          name: block.name || block.type,
                          config: block.config,
                          enableClose: false,
                        };
                      }),
                    },
                    {
                      type: "row",
                      weight: [...topRowLeftBlock, ...topRowCenterBlock, ...topRowRightBlock].length > 0 ? ([...topRowLeftBlock, ...topRowCenterBlock, ...topRowRightBlock][0].config?.weight || 100) : 100,
                      children: [
                        {
                          type: "tabset",
                          id: tabset.id,
                          weight: topRowLeftBlock.length > 0 ? (topRowLeftBlock[0].config?.weight || 100) : 100,
                          children: topRowLeftBlock.map((block, blockIndex) => {
                            const blockId = block.id || uuid();
                            pushTab({
                              type: block.type,
                              id: blockId,
                            });
                            return {
                              type: "tab",
                              id: blockId,
                              component: block.type,
                              name: block.name || block.type,
                              config: block.config,
                              enableClose: false,
                            };
                          }),
                        },
                        {
                          type: "tabset",
                          id: tabset.id,
                          weight: topRowCenterBlock.length > 0 ? (topRowCenterBlock[0].config?.weight || 100) : 100,
                          children: topRowCenterBlock.map((block, blockIndex) => {
                            const blockId = block.id || uuid();
                            pushTab({
                              type: block.type,
                              id: blockId,
                            });
                            return {
                              type: "tab",
                              id: blockId,
                              component: block.type,
                              name: block.name || block.type,
                              config: block.config,
                              enableClose: false,
                            };
                          }),
                        },
                        {
                          type: "tabset",
                          id: tabset.id,
                          weight: topRowRightBlock.length > 0 ? (topRowRightBlock[0].config?.weight || 100) : 100,
                          children: topRowRightBlock.map((block, blockIndex) => {
                            const blockId = block.id || uuid();
                            pushTab({
                              type: block.type,
                              id: blockId,
                            });
                            return {
                              type: "tab",
                              id: blockId,
                              component: block.type,
                              name: block.name || block.type,
                              config: block.config,
                              enableClose: false,
                            };
                          }),
                        }
                      ]
                    },
                    {
                      type: "tabset",
                      id: tabset.id,
                      weight: topRowBottomBlock.length > 0 ? (topRowBottomBlock[0].config?.weight || 100) : 100,
                      children: topRowBottomBlock.map((block, blockIndex) => {
                        const blockId = block.id || uuid();
                        pushTab({
                          type: block.type,
                          id: blockId,
                        });
                        return {
                          type: "tab",
                          id: blockId,
                          component: block.type,
                          name: block.name || block.type,
                          config: block.config,
                          enableClose: false,
                        };
                      }),
                    }
                  ]
                }
              ]
            }
          )
        });

        const centerRowChildrens: any[] = [];
        leftRow.forEach((tabset, index) => {
          centerRowChildrens.push(
            {
              type: "row",
              weight: tabset.weight || 100,
              children: [
                {
                  type: "tabset",
                  id: tabset.id,
                  weight: leftRowTopBlock.length > 0 ? (leftRowTopBlock[0].config?.weight || 100) : 100,
                  children: leftRowTopBlock.map((block, blockIndex) => {
                    const blockId = block.id || uuid();
                    pushTab({
                      type: block.type,
                      id: blockId,
                    });
                    return {
                      type: "tab",
                      id: blockId,
                      component: block.type,
                      name: block.name || block.type,
                      config: block.config,
                      enableClose: false,
                    };
                  }),
                },
                {
                  type: "row",
                  weight: [...leftRowLeftBlock, ...leftRowCenterBlock, ...leftRowRightBlock].length > 0 ? ([...leftRowLeftBlock, ...leftRowCenterBlock, ...leftRowRightBlock][0].config?.weight || 100) : 100,
                  children: [
                    {
                      type: "tabset",
                      id: tabset.id,
                      weight: leftRowLeftBlock.length > 0 ? (leftRowLeftBlock[0].config?.weight || 100) : 100,
                      children: leftRowLeftBlock.map((block, blockIndex) => {
                        const blockId = block.id || uuid();
                        pushTab({
                          type: block.type,
                          id: blockId,
                        });
                        return {
                          type: "tab",
                          id: blockId,
                          component: block.type,
                          name: block.name || block.type,
                          config: block.config,
                          enableClose: false,
                        };
                      }),
                    },
                    {
                      type: "tabset",
                      id: tabset.id,
                      weight: leftRowCenterBlock.length > 0 ? (leftRowCenterBlock[0].config?.weight || 100) : 100,
                      children: leftRowCenterBlock.map((block, blockIndex) => {
                        const blockId = block.id || uuid();
                        pushTab({
                          type: block.type,
                          id: blockId,
                        });
                        return {
                          type: "tab",
                          id: blockId,
                          component: block.type,
                          name: block.name || block.type,
                          config: block.config,
                          enableClose: false,
                        };
                      }),
                    },
                    {
                      type: "tabset",
                      id: tabset.id,
                      weight: leftRowRightBlock.length > 0 ? (leftRowRightBlock[0].config?.weight || 100) : 100,
                      children: leftRowRightBlock.map((block, blockIndex) => {
                        const blockId = block.id || uuid();
                        pushTab({
                          type: block.type,
                          id: blockId,
                        });
                        return {
                          type: "tab",
                          id: blockId,
                          component: block.type,
                          name: block.name || block.type,
                          config: block.config,
                          enableClose: false,
                        };
                      }),
                    }
                  ]
                },
                {
                  type: "tabset",
                  id: tabset.id,
                  weight: leftRowBottomBlock.length > 0 ? (leftRowBottomBlock[0].config?.weight || 100) : 100,
                  children: leftRowBottomBlock.map((block, blockIndex) => {
                    const blockId = block.id || uuid();
                    pushTab({
                      type: block.type,
                      id: blockId,
                    });
                    return {
                      type: "tab",
                      id: blockId,
                      component: block.type,
                      name: block.name || block.type,
                      config: block.config,
                      enableClose: false,
                    };
                  }),
                }
              ]
            }
          )
        });

        centerRow.forEach((tabset, index) => {
          centerRowChildrens.push(
            {

              type: "row",
              weight: tabset.weight || 100,
              children: [
                {
                  type: "tabset",
                  id: tabset.id,
                  weight: centerRowTopBlock.length > 0 ? (centerRowTopBlock[0].config?.weight || 100) : 100,
                  children: centerRowTopBlock.map((block, blockIndex) => {
                    const blockId = block.id || uuid();
                    pushTab({
                      type: block.type,
                      id: blockId,
                    });
                    return {
                      type: "tab",
                      id: blockId,
                      component: block.type,
                      name: block.name || block.type,
                      config: block.config,
                      enableClose: false,
                    };
                  }),
                },
                {
                  type: "row",
                  weight: [...centerRowLeftBlock, ...centerRowCenterBlock, ...centerRowRightBlock].length > 0 ? ([...centerRowLeftBlock, ...centerRowCenterBlock, ...centerRowRightBlock][0].config?.weight || 100) : 100,
                  children: [
                    {
                      type: "tabset",
                      id: tabset.id,
                      weight: centerRowLeftBlock.length > 0 ? (centerRowLeftBlock[0].config?.weight || 100) : 100,
                      children: centerRowLeftBlock.map((block, blockIndex) => {
                        const blockId = block.id || uuid();
                        pushTab({
                          type: block.type,
                          id: blockId,
                        });
                        return {
                          type: "tab",
                          id: blockId,
                          component: block.type,
                          name: block.name || block.type,
                          config: block.config,
                          enableClose: false,
                        };
                      }),
                    },
                    {
                      type: "tabset",
                      id: tabset.id,
                      weight: centerRowCenterBlock.length > 0 ? (centerRowCenterBlock[0].config?.weight || 100) : 100,
                      children: centerRowCenterBlock.map((block, blockIndex) => {
                        const blockId = block.id || uuid();
                        pushTab({
                          type: block.type,
                          id: blockId,
                        });
                        return {
                          type: "tab",
                          id: blockId,
                          component: block.type,
                          name: block.name || block.type,
                          config: block.config,
                          enableClose: false,
                        };
                      }),
                    },
                    {
                      type: "tabset",
                      id: tabset.id,
                      weight: centerRowRightBlock.length > 0 ? (centerRowRightBlock[0].config?.weight || 100) : 100,
                      children: centerRowRightBlock.map((block, blockIndex) => {
                        const blockId = block.id || uuid();
                        pushTab({
                          type: block.type,
                          id: blockId,
                        });
                        return {
                          type: "tab",
                          id: blockId,
                          component: block.type,
                          name: block.name || block.type,
                          config: block.config,
                          enableClose: false,
                        };
                      }),
                    }
                  ]
                },
                {
                  type: "tabset",
                  id: tabset.id,
                  weight: centerRowBottomBlock.length > 0 ? (centerRowBottomBlock[0].config?.weight || 100) : 100,
                  children: centerRowBottomBlock.map((block, blockIndex) => {
                    const blockId = block.id || uuid();
                    pushTab({
                      type: block.type,
                      id: blockId,
                    });
                    return {
                      type: "tab",
                      id: blockId,
                      component: block.type,
                      name: block.name || block.type,
                      config: block.config,
                      enableClose: false,
                    };
                  }),
                }
              ]
            }
          )
        });

        rightRow.forEach((tabset, index) => {
          centerRowChildrens.push(
            {
              type: "row",
              weight: tabset.weight || 100,
              children: [
                {
                  type: "tabset",
                  id: tabset.id,
                  weight: rightRowTopBlock.length > 0 ? (rightRowTopBlock[0].config?.weight || 100) : 100,
                  children: rightRowTopBlock.map((block, blockIndex) => {
                    const blockId = block.id || uuid();
                    pushTab({
                      type: block.type,
                      id: blockId,
                    });
                    return {
                      type: "tab",
                      id: blockId,
                      component: block.type,
                      name: block.name || block.type,
                      config: block.config,
                      enableClose: false,
                    };
                  }),
                },
                {
                  type: "row",
                  weight: [...rightRowLeftBlock, ...rightRowCenterBlock, ...rightRowRightBlock].length > 0 ? ([...rightRowLeftBlock, ...rightRowCenterBlock, ...rightRowRightBlock][0].config?.weight || 100) : 100,
                  children: [
                    {
                      type: "tabset",
                      id: tabset.id,
                      weight: rightRowLeftBlock.length > 0 ? (rightRowLeftBlock[0].config?.weight || 100) : 100,
                      children: rightRowLeftBlock.map((block, blockIndex) => {
                        const blockId = block.id || uuid();
                        pushTab({
                          type: block.type,
                          id: blockId,
                        });
                        return {
                          type: "tab",
                          id: blockId,
                          component: block.type,
                          name: block.name || block.type,
                          config: block.config,
                          enableClose: false,
                        };
                      }),
                    },
                    {
                      type: "tabset",
                      id: tabset.id,
                      weight: rightRowCenterBlock.length > 0 ? (rightRowCenterBlock[0].config?.weight || 100) : 100,
                      children: rightRowCenterBlock.map((block, blockIndex) => {
                        const blockId = block.id || uuid();
                        pushTab({
                          type: block.type,
                          id: blockId,
                        });
                        return {
                          type: "tab",
                          id: blockId,
                          component: block.type,
                          name: block.name || block.type,
                          config: block.config,
                          enableClose: false,
                        };
                      }),
                    },
                    {
                      type: "tabset",
                      id: tabset.id,
                      weight: rightRowRightBlock.length > 0 ? (rightRowRightBlock[0].config?.weight || 100) : 100,
                      children: rightRowRightBlock.map((block, blockIndex) => {
                        const blockId = block.id || uuid();
                        pushTab({
                          type: block.type,
                          id: blockId,
                        });
                        return {
                          type: "tab",
                          id: blockId,
                          component: block.type,
                          name: block.name || block.type,
                          config: block.config,
                          enableClose: false,
                        };
                      }),
                    }
                  ]
                },
                {
                  type: "tabset",
                  id: tabset.id,
                  weight: rightRowBottomBlock.length > 0 ? (rightRowBottomBlock[0].config?.weight || 100) : 100,
                  children: rightRowBottomBlock.map((block, blockIndex) => {
                    const blockId = block.id || uuid();
                    pushTab({
                      type: block.type,
                      id: blockId,
                    });
                    return {
                      type: "tab",
                      id: blockId,
                      component: block.type,
                      name: block.name || block.type,
                      config: block.config,
                      enableClose: false,
                    };
                  }),
                }
              ]

            }
          )
        });

        modelJson.layout.children.push(
          {
            type: "row",
            weight: 100,
            children: centerRowChildrens
          }
        )

        bottomRow.forEach((tabset, index) => {
          modelJson.layout.children.push(
            {
              type: "row",
              weight: tabset.weight || 100,
              children: [
                {
                  type: "row",
                  weight: 100,
                  children: [
                    {
                      type: "tabset",
                      id: tabset.id,
                      weight: bottomRowTopBlock.length > 0 ? (bottomRowTopBlock[0].config?.weight || 100) : 100,
                      children: bottomRowTopBlock.map((block, blockIndex) => {
                        const blockId = block.id || uuid();
                        pushTab({
                          type: block.type,
                          id: blockId,
                        });
                        return {
                          type: "tab",
                          id: blockId,
                          component: block.type,
                          name: block.name || block.type,
                          config: block.config,
                          enableClose: false,
                        };
                      }),
                    },
                    {
                      type: "row",
                      weight: [...bottomRowLeftBlock, ...bottomRowCenterBlock, ...bottomRowRightBlock].length > 0 ? ([...bottomRowLeftBlock, ...bottomRowCenterBlock, ...bottomRowRightBlock][0].config?.weight || 100) : 100,
                      children: [
                        {
                          type: "tabset",
                          id: tabset.id,
                          weight: bottomRowLeftBlock.length > 0 ? (bottomRowLeftBlock[0].config?.weight || 100) : 100,
                          children: bottomRowLeftBlock.map((block, blockIndex) => {
                            const blockId = block.id || uuid();
                            pushTab({
                              type: block.type,
                              id: blockId,
                            });
                            return {
                              type: "tab",
                              id: blockId,
                              component: block.type,
                              name: block.name || block.type,
                              config: block.config,
                              enableClose: false,
                            };
                          }),
                        },
                        {
                          type: "tabset",
                          id: tabset.id,
                          weight: bottomRowCenterBlock.length > 0 ? (bottomRowCenterBlock[0].config?.weight || 100) : 100,
                          children: bottomRowCenterBlock.map((block, blockIndex) => {
                            const blockId = block.id || uuid();
                            pushTab({
                              type: block.type,
                              id: blockId,
                            });
                            return {
                              type: "tab",
                              id: blockId,
                              component: block.type,
                              name: block.name || block.type,
                              config: block.config,
                              enableClose: false,
                            };
                          }),
                        },
                        {
                          type: "tabset",
                          id: tabset.id,
                          weight: bottomRowRightBlock.length > 0 ? (bottomRowRightBlock[0].config?.weight || 100) : 100,
                          children: bottomRowRightBlock.map((block, blockIndex) => {
                            const blockId = block.id || uuid();
                            pushTab({
                              type: block.type,
                              id: blockId,
                            });
                            return {
                              type: "tab",
                              id: blockId,
                              component: block.type,
                              name: block.name || block.type,
                              config: block.config,
                              enableClose: false,
                            };
                          }),
                        }
                      ]
                    },
                    {
                      type: "tabset",
                      id: tabset.id,
                      weight: bottomRowBottomBlock.length > 0 ? (bottomRowBottomBlock[0].config?.weight || 100) : 100,
                      children: bottomRowBottomBlock.map((block, blockIndex) => {
                        const blockId = block.id || uuid();
                        pushTab({
                          type: block.type,
                          id: blockId,
                        });
                        return {
                          type: "tab",
                          id: blockId,
                          component: block.type,
                          name: block.name || block.type,
                          config: block.config,
                          enableClose: false,
                        };
                      }),
                    }
                  ]
                }
              ]
            }
          )
        });
        modelJson.layout.children = [
          {
            type: "row",
            weight: 100,
            children: modelJson.layout.children
          }
        ]
        const newModel = Model.fromJson(modelJson);
        set({
          layout: {
            ...get().layout,
            modelJson: modelJson,
            model: newModel
          },
        });
      }
    }

  },

  blocklyCodePath: "",
  setBlocklyCodePath: (path: string) => {
    set({ blocklyCodePath: path });
  },

  pythonCodePath: "",
  setPythonCodePath: (path: string) => {
    set({ pythonCodePath: path });
  },

  blocklyVariables: {},
  setBlocklyVariables: ({ scope, state, newData }) =>
    set({
      blocklyVariables: {
        ...get().blocklyVariables,
        [scope]: { ...get().blocklyVariables[scope], [state]: newData },
      },
    }),

  getBlocklyVariables: (scope: string, state: string) => {
    return get().blocklyVariables[scope]?.[state];
  },
  initBlocklyVariables: (d) => {
    set({ blocklyVariables: d || {} });
  },
  subscribeBlocklyVariables: (callback) => {
    return useEditorState.subscribe((state, prevState) => {
      if (state.blocklyVariables !== prevState.blocklyVariables) {
        callback(state.blocklyVariables);
      }
    });
  },

  toJson: () => {
    const excludeKeys = ["godotContainerRef", "documentationRefs", "initBlocksFile", "generateBlocksFile", "toolboxFile", "prevEditorState", "achievementMapping"];
    const state = get();
    return JSON.stringify(state, (key, value) => {
      try {
        if (excludeKeys.includes(key)) return undefined;
        if (typeof value === "function") return undefined;
        if (key === "layout") {
          return {
            ...value,
            modelJson: value?.model?.toJson ? value.model.toJson() : {},
            model: undefined,
          };
        }
        if (key === "achievements") {
          const filteredAchievments = value?.map((a: any) => {
            return {
              id: a.id,
              is_achieved: a.is_achieved
            }
          });
          return get().encryptMessage(filteredAchievments)
        }

        if (key === "ltiQuizResults") {
          return get().encryptMessage(value);
        }

        return value;
      } catch (e) {
        console.error(e);
        return undefined;
      }
    });
  },

  authorize: async () => {
    if (get().environment === "local" || !usesRidersAuth(get().platform) || isPublicProject()) {
      return;
    }

    // Riders auth check
    try {
      const resp = await axios.get(
        `${get().verifyResponse.api_url}/api/v1/editor-session/`,
        {
          withCredentials: true,
        },
      );

      const activeSessions = resp.data.map((s: any) => s.id);
      if (activeSessions.includes(Number(get().metadata?.session_id))) {
        return;
      } else {
        throw new SessionError("invalidToken.sessionExpired");
      }
    } catch (error) {
      throw new SessionError("invalidToken.loginRequired", error as Error);
    }
  },

  codeTabWidth: 30,
  setCodeTabWidth: (width: number) => {
    if (!width) return;
    set({ codeTabWidth: width });
  },

  codeTabHeight: 65,
  setCodeTabHeight: (height: number) => {
    if (height === 0) return;
    set({ codeTabHeight: height });
  },

  saveStatus: "SAVED",
  setSaveStatus(status) {
    set({ saveStatus: status });
  },

  generateBlocksFile: "",
  initBlocksFile: "",
  toolboxFile: "",

  backwardCompatibility: false,

  yamlData: undefined,
  initYamlData: async () => {
    const { project, setYamlData, environment } = get();
    //TODO(Bautaray):: After complete the development add environemnt check to this check.
    if (!project?.project_id) return;

    if (environment === "local") {
      setYamlData(localYaml);
      return
    }
    const yamlData = await getYaml(project.project_id);
    setYamlData(yamlData);
  },
  setYamlData: (yamlData: string | object) => {
    const parsedYaml = typeof yamlData === "string" ? parse(yamlData) : yamlData;
    set({ yamlData: parsedYaml });
    console.log(parsedYaml);
  },

  progressedChapters: [],
  initProgressedChapters: async () => {
    const { project, tokens: { express_api_token } = {}, environment } = get();
    if (!project?.project_id || !express_api_token || environment === "local") return;
    const moduleProgresses = await getModuleProgresses(express_api_token);
    const progressedChapters = moduleProgresses.titles.map((t: string) => parseFloat(t)).filter((t: number) => t > 0)
    set({ progressedChapters });
  },

  collapsedSidebar: false,
  setCollapsedSidebar: (collapsedSidebar: boolean) => {
    set({ collapsedSidebar });
  },

  configJson: undefined,
  initConfigJson: async () => {
    const { project } = get();
    //TODO(Bautaray):: After complete the development add environemnt check to this check.
    if (!project?.project_id) return;
    const configData = await getConfigJson(project.project_id);
    set({ configJson: configData })

  },

  initBlocklyFiles: async () => {
    try {
      const { configJson, backwardCompatibility, language } = get();
      //TODO(Bautaray):: After complete the development add environemnt check to this check.
      if (!configJson?.toolboxPath || !backwardCompatibility || configJson.dev === "code") return;

      const ApiUrl = "https://sim.riders.ai"
      let toolboxData = "";
      if (configJson.language === "cpp" || configJson.language === "ino") {
        toolboxData = await fetch(
          `${ApiUrl}/blockly/stembox/riders-blocks/toolbox.json`
        ).then((res) => {
          if (res.ok) return res.text();
          else return "";
        });
      } else {
        toolboxData = await fetch(
          `${ApiUrl}/${configJson.toolboxPath.replace(
            "{language}",
            language?.toLowerCase()
          )}`
        ).then((res) => {
          if (res.ok) return res.text();
          else return "";
        });
      }
      set({ toolboxFile: toolboxData });

      let initializerURL = "";
      let generatorURL = "";
      if (configJson.language === "cpp" || configJson.language === "ino") {
        generatorURL = ApiUrl + "/blockly/stembox/riders-blocks/generateblocks.js";
        const generatorFile = await fetch(generatorURL).then((res) => {
          if (res.ok) return res.text();
          else return "";
        });
        set({ initBlocksFile: "", generateBlocksFile: generatorFile });

      } else if (configJson?.folderUrl) {
        initializerURL = configJson.folderUrl + "initblocks.js";
        generatorURL = configJson.folderUrl + "generateblocks.js";

        if (configJson.toolboxPath.includes("{language}")) {
          const path = configJson.toolboxPath.split("/").slice(0, -1).join("/");
          initializerURL =
            ApiUrl +
            "/" +
            path.replace("{language}", language.toLowerCase()) +
            "/initblocks.js";
          generatorURL =
            ApiUrl +
            "/" +
            path.replace("{language}", language.toLowerCase()) +
            "/generateblocks.js";
        }
        const initializerFile = await fetch(initializerURL).then((res) => {
          if (res.ok) return res.text();
          else return "";
        });
        const generatorFile = await fetch(generatorURL).then((res) => {
          if (res.ok) return res.text();
          else return "";
        });
        set({ initBlocksFile: initializerFile, generateBlocksFile: generatorFile });
      }

    } catch (e) {
      // Do nothing
    }
  },

  circleFlow: null,
  setCircleFlow: (circleFlow) => {
    set({ circleFlow });
  },

  circleFlowPendingIndex: null,
  setCircleFlowPendingIndex: (circleFlowPendingIndex) => {
    set({ circleFlowPendingIndex });
  },
  circleFlowGroupNavigatePending: false,
  setCircleFlowGroupNavigatePending: (circleFlowGroupNavigatePending) => {
    set({ circleFlowGroupNavigatePending });
  },

  godotVersionedBuild: false,
  godotLoadingComplete: false,

  blocklyState: "NOTINITED",
  highLightQueue: [],
  highlightBlock: async (message: any) => {
    const { blockId } = message;
    const w = (window as any).mainWorkspace;
    if (w) {
      w.highlightBlock(blockId);
    }
  },

  highlightToolboxBlock: async (message: any) => {
    const { category, block } = message;
    try {
      const isExisting = window.document.querySelector(
        ".blockly-flyout-highlight",
      );

      if (isExisting) {
        isExisting.remove();
      }

      if (category === -1) {
        isExisting?.remove();
        return;
      }

      const workspace = (window as any).mainWorkspace;
      const toolbox = workspace.getToolbox();

      if (toolbox) {
        const selected = toolbox.getSelectedItem();
        const target = toolbox.getToolboxItems()[category];

        if (!selected || !(selected.id_ === target.id_)) {
          toolbox.setSelectedItem(target);
        }
      }

      const flyout = (window as any).mainWorkspace?.getFlyout();
      const blocks = flyout.getWorkspace().getTopBlocks(false);

      const b = blocks[block];

      //flyoutToolbox
      if (toolbox === null) {
        const blockPos = b?.getRelativeToSurfaceXY();
        const flyout = workspace?.flyout
        const scale = workspace?.scale;
        if (flyout && scale && blockPos) {
          const flyoutHeight = flyout.getHeight();
          const scrollTarget = -blockPos.y * scale + (flyoutHeight / 2);
          workspace.flyout.getWorkspace()?.scroll(0, scrollTarget);
        }
      }

      const svg = b.getSvgRoot();
      const svgGroup = svg.closest("g");

      const bbox = svg.getBBox();

      const rect = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "rect",
      );

      rect.setAttribute("x", (parseInt(bbox.x) - 10).toString());
      rect.setAttribute("y", (parseInt(bbox.x) - 10).toString());
      rect.setAttribute("width", bbox.width + 20);
      rect.setAttribute("height", bbox.height + 20);
      rect.setAttribute("rx", "12");
      rect.setAttribute("ry", "12");
      rect.setAttribute("fill", "#5EDDA6");
      rect.setAttribute("class", "blockly-flyout-highlight");

      svgGroup.insertBefore(rect, svgGroup.firstChild);
    } catch (error) {
      Sentry.captureException(new BlocklyHighlightError("Failed to highlight toolbox block", error as Error));
    }
  },

  setBlocklyState: (blocklyState: "NOTINITED" | "REFRESHING" | "INITED") => {
    const { highLightQueue, highlightBlock, highlightToolboxBlock } = get();
    if (blocklyState === "INITED" && highLightQueue.length > 0) {
      highLightQueue.forEach((message: any) => {
        if (message?.action === "highlightBlock") {
          highlightBlock(message);
        } else if (message?.action === "highlightToolboxBlock") {
          highlightToolboxBlock(message);
        }
      });
      set({ highLightQueue: [] });
    }
    set({ blocklyState });
  },

  prevEditorState: {},
  ltiAchievementRestore: () => {
    const { achievements, platform } = get();
    if (!achievements || platform !== "lti") return;

    const { achievements: prevAchievements } = get().prevEditorState;
    if (!prevAchievements) return;

    //Restore is_achieved status from prevAchievements.
    const updatedAchievements = achievements.map((achievement) => {
      const prev = prevAchievements.find((p: any) => p.id === achievement.id);
      if (prev) {
        return {
          ...achievement,
          is_achieved: prev.is_achieved
        };
      }
      return achievement;
    });
    set({ achievements: updatedAchievements });
  },

  encryptMessage: (message: any) => {
    const encryptor = createEncryptor(
      import.meta.env.VITE_ENCRYPTION_KEY || "",
    );
    if (typeof message === "string") {
      return encryptor.encrypt(message);
    } else if (typeof message === "object") {
      return encryptor.encrypt(JSON.stringify(message));
    }
    return "ERROR";
  },

  decryptMessage: (message: any) => {
    const encryptor = createEncryptor(
      import.meta.env.VITE_ENCRYPTION_KEY || "",
    );
    if (typeof message === "string") {
      return encryptor.decrypt(message);
    } else if (typeof message === "object") {
      return encryptor.decrypt(JSON.stringify(message));
    }
    return "ERROR";
  },

  setPrevEditorState: (prevEditorState: any) => {
    if (!prevEditorState) return;

    const previousState = JSON.parse(prevEditorState);
    if (previousState.achievements) {
      previousState.achievements = JSON.parse(get().decryptMessage(previousState.achievements))
    }
    if (previousState.ltiQuizResults) {
      previousState.ltiQuizResults = JSON.parse(get().decryptMessage(previousState.ltiQuizResults))
    }
    set({ prevEditorState: previousState });

    // Things needs to be restored after setting prevEditorState.
    get().setCodeTabWidth(previousState.codeTabWidth);
    get().setCodeTabHeight(previousState.codeTabHeight);
    get().ltiAchievementRestore();
    get().initLtiQuizResults();
  },

  achievementMapping: {},
  initAchievementMapping: async () => {
    set({ achievementMapping: achievementMappingJson });
  },

  parseAchievementId: (messageData: any) => {
    let id: string | number = parseInt(messageData);
    if (!isNaN(id)) {
      // achievementId is integer
      id = get().achievementMapping[String(id)] || id;
    } else {
      // AchievementId is string
      id = messageData;
    }
    return id;
  },

  qaMapping: {},
  initQAMapping: async () => {
    set({ qaMapping: qaMappingJson });
  },

  parseQAId: (messageData: any) => {
    let id: string | number = parseInt(messageData);
    if (!isNaN(id)) {
      // qaId is integer
      id = get().qaMapping[String(id)] || id;
    } else {
      // QAId is string
      id = messageData;
    }
    return id;
  },

  ltiQuizResults: {}, // quizId -> { question: number, answer: number, correct_answer: number }[]
  initLtiQuizResults: async () => {
    const { prevEditorState } = get();
    if (prevEditorState?.ltiQuizResults) {
      set({ ltiQuizResults: prevEditorState.ltiQuizResults });
    }
  },

  setLtiQuizResults: (quizId: string, results: { question: number, answer: number, correct_answer: number }[]) => {
    const { ltiQuizResults } = get();
    const newResults = { ...ltiQuizResults, [quizId]: results };
    set({ ltiQuizResults: newResults });
  },

  calculateLtiScore: () => {
    const { ltiQuizResults, ltiQuizzesList } = get();
    let { achievements } = get();

    let score = 0;
    achievements = achievements || [];
    const activityPoint = (achievements.length + ltiQuizzesList.length) > 0
      ? 100 / (achievements.length + ltiQuizzesList.length)
      : 0;

    const completedAchievementCount = achievements.filter(
      (a: any) => a.is_achieved
    ).length;

    score += completedAchievementCount * activityPoint;

    Object.keys(ltiQuizResults).forEach((quizId) => {
      const results = ltiQuizResults[quizId];

      console.log(`results: ${results}, typeof ${typeof results} `);
      const correctCount = results.filter(
        (r) => r.answer === r.correct_answer
      ).length;
      score += (correctCount / results.length) * activityPoint;
    });

    return score;
  },

  ltiQuizzesList: [],
  initLtiQuizzesList: async () => {
    if (get().platform !== "lti") return;

    const token = get().tokens?.express_api_token;
    const list = await getLTIQuizzesList(token || "") as string[];
    set({ ltiQuizzesList: list || [] });
  },

  initGodotVersionCheck: async () => {
    // v3 builds predate the godotLoadingProgress/godotLoadingComplete
    // protocol and never emit these events, so don't wait on them.
    if (get().project?.backwardCompatibility) return;

    const baseUrl = getGodotBaseUrl(get().project?.stembox_branch);
    try {
      const res = await fetch(`${baseUrl}/version.json`);
      if (res.ok) set({ godotVersionedBuild: true });
    } catch {
      // version.json missing/unreachable -> fall back to legacy boot behavior
    }
  },
}));
